/**
 * Financial Model detail — full page (mirrors the PPA ContractDetail pattern).
 *
 * Route: /portfolios/:portfolioId/financial-models/:modelId
 *
 * Sections: header (scenario / status / Active + Activate / Delete),
 * DSCR trajectory chart, model assumption cards + result tables
 * (generation, revenue, costs, CFADS, debt, DSCR, prices),
 * and the audited override history (for any prior overrides).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Chip, Button, IconButton, Tooltip, Alert,
  CircularProgress, Card, CardContent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LineChart } from '@mui/x-charts/LineChart';
import axios from 'axios';

import ModelResultTables from './ModelResultTables';

const API = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';
export default function FinancialModelDetail() {
  const { portfolioId, modelId } = useParams();
  const navigate = useNavigate();
  const base = `${API}/api/portfolios/${portfolioId}/financial-models`;
  const backTo = `/portfolios/${portfolioId}/contracts`;

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${base}/${modelId}`);
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to load model');
    }
  }, [base, modelId]);

  useEffect(() => { load(); }, [load]);

  // Poll while still parsing in the background.
  useEffect(() => {
    if (data?.status !== 'uploaded') return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [data, load]);

  const activate = async () => {
    setBusy(true);
    try { await axios.post(`${base}/${modelId}/activate`); await load(); }
    catch (e) { setErr(e.response?.data?.error || 'Activate failed'); }
    finally { setBusy(false); }
  };
  const reparse = async () => {
    // Re-runs the parser against the .xlsb already in GCS. Use after
    // a parser upgrade so the model picks up the new extraction
    // without re-uploading the workbook.
    setBusy(true);
    try {
      await axios.post(`${base}/${modelId}/reparse`);
      await load();   // status flips to 'uploaded' → existing poll catches the transition
    } catch (e) {
      setErr(e.response?.data?.error || 'Reparse failed');
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    try { await axios.delete(`${base}/${modelId}`); navigate(backTo); }
    catch (e) { setErr(e.response?.data?.error || 'Delete failed'); }
  };

  if (!data && !err) {
    return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }

  const eff = data?.effective;
  const candidates = data?.remap_candidates || [];
  const series = (eff?.dscr_series || []).slice(0, 120);

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backTo)} sx={{ mb: 2 }}>
        Back to Documents
      </Button>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}

      {/* Header — title left, actions right. Title row uses minWidth:0 so
          the filename can ellipsis instead of pushing the action buttons
          onto a second line. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            {data?.is_active && (
              <Tooltip title="Active — drives Facility / DSCR / Variance">
                <CheckCircleIcon sx={{ color: '#1f9d57', flexShrink: 0 }} />
              </Tooltip>
            )}
            <Typography
              noWrap
              sx={{
                fontSize: '1.3rem', fontWeight: 700,
                minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            >
              {data?.label || data?.filename || 'Financial model'}
            </Typography>
            <Chip
              size="small"
              label={data?.is_active ? 'ACTIVE' : data?.status}
              sx={{
                flexShrink: 0,
                fontWeight: 700, fontSize: '0.66rem',
                bgcolor: data?.is_active ? 'rgba(34,160,90,0.14)' : 'rgba(59,130,246,0.10)',
                color: data?.is_active ? '#1f9d57' : '#2d7dd2',
              }}
            />
          </Stack>
          <Typography
            noWrap
            sx={{
              fontSize: '0.82rem', color: 'text.secondary', mt: 0.5,
              overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {data?.filename}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {!data?.is_active && data?.status === 'parsed' && (
            <Button variant="contained" disabled={busy} onClick={activate}>
              Activate
            </Button>
          )}
          <Tooltip title={data?.status === 'uploaded'
              ? 'Re-parsing…'
              : 'Re-parse (apply latest parser to the .xlsb already in GCS)'}>
            <span>
              <IconButton disabled={busy || data?.status === 'uploaded'}
                          onClick={reparse}>
                {data?.status === 'uploaded'
                  ? <CircularProgress size={20} thickness={5} />
                  : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete model">
            <IconButton onClick={remove} sx={{ '&:hover': { color: 'error.main' } }}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {data?.status === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }}>Parse failed: {data?.error}</Alert>
      )}
      {data?.status === 'uploaded' && (
        <Alert severity="info" sx={{ mb: 2 }}>Parsing the workbook…</Alert>
      )}

      {data?.status === 'parsed' && (
        <>
          {/* KPI strip removed — every value lived elsewhere in cleaner
              context (assumption cards: covenants, debt pricing, yield)
              and two were misleading (CFADS total was life-of-project
              without label; "Debt o/s" was an end-of-life residual from
              an oversimplified balance calc, not current outstanding). */}

          {eff?.warnings?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {eff.warnings.join(' · ')}
            </Alert>
          )}

          {series.length > 0 && (
            <Card variant="outlined" sx={{ mb: 3, borderColor: 'divider' }}>
              <CardContent>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1 }}>
                  DSCR trajectory
                </Typography>
                <LineChart
                  height={240}
                  xAxis={[{ scaleType: 'point', data: series.map((p) => p.period_start) }]}
                  series={[{ data: series.map((p) => p.dscr), showMark: false, label: 'DSCR' }]}
                />
              </CardContent>
            </Card>
          )}

          {/* Actual-vs-forecast comparison moved to the Facility &
              Covenants screen (it's the natural home for performance
              vs the lender financial model). */}

          {/* Model assumptions + result tables (generation, revenue, costs,
              CFADS, debt service, DSCR, prices) — straight from the
              workbook (with overrides applied), nothing fabricated. */}
          <ModelResultTables effective={eff} />

          {/* Audit history */}
          {!!(data.overrides || []).length && (
            <Card variant="outlined" sx={{ borderColor: 'divider' }}>
              <CardContent>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1 }}>
                  Override history
                </Typography>
                <Stack spacing={0.5}>
                  {data.overrides.map((o) => (
                    <Typography key={o.id} sx={{ fontSize: '0.72rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                      {new Date(o.edited_at).toLocaleString('en-AU')} · {o.kind}:{o.target} = {JSON.stringify(o.value)}
                      {o.reason ? ` · ${o.reason}` : ''}
                    </Typography>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}
